import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import ProductMain from "../layouts/main/ProductMain";
import { ProductContext } from "../context/ProductContext";
import Footer from "../layouts/footer/Footer";
import Header from "../layouts/header/Header";
import ProductSlider from "../components/product/ProductSlider";
import { DEFAULT_CURRENCY, transportMethods } from "../utils/global_const";
import ProductSliderHorizontal from "../components/product/ProductSliderHorizontal";
import { useParams } from "react-router-dom";
import descriptions from "../descriptions.json";

const Container = styled.div`
  background-color: var(--background-color);
  color: var(--text-color);
`;
const RecommendedContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
const RecommendedWrapper = styled.div`
  margin-bottom: 20px;
  width: 94%;
  @media (min-width: 1025px) {
    max-width: var(--max-width-container);
  }
`;

const Product = () => {
  const { t, i18n } = useTranslation();
  const { product, recommendedProducts } = useContext(ProductContext);
  const { slug } = useParams();

  const [currencyCode, setCurrencyCode] = useState(
    localStorage.getItem("currency") || DEFAULT_CURRENCY
  );

  // Generiši canonical URL na snusco.com
  const productUrl = product
    ? `https://www.snusco.com/${i18n.language}/${slug}`
    : "https://www.snusco.com";
  const enUrl = product ? `https://www.snusco.com/en/${slug}` : "https://www.snusco.com";
  const deUrl = product ? `https://www.snusco.com/de/${slug}` : "https://www.snusco.com";

  // Odredi jezik za i18n
  const lang = i18n.language.startsWith("sr")
    ? "sr"
    : i18n.language.startsWith("de")
    ? "de"
    : "en";
  const shortKey = `short_${lang}`;
  // Pronađi opis za trenutni proizvod
  const descItem = descriptions.find((d) => d.slug === slug) || {};
  const metaDescription = descItem[shortKey] || "";

  // Napravi keywords polje na osnovu atributa
  const keywords = [
    product?.category_name,
    product?.name,
    product?.flavor,
    product?.nicotine ? `${product.nicotine}mg` : null,
    product?.format,
    "nicotine pouches",
  ]
    .filter(Boolean)
    .join(", ");

  // OG/Twitter slika
  const ogImageObj =
    product?.images?.find((img) => img.is_primary) || product?.images?.[0] || null;
  const ogImage = ogImageObj?.large || null;

  // Schema.org ne poznaje naš "on_request" - mapiramo ga na BackOrder
  // (dostupno, ali se naručuje), da JSON-LD ne bi lažno tvrdio InStock za
  // proizvode koji trenutno nisu na stanju.
  const AVAILABILITY_MAP = {
    in_stock: "https://schema.org/InStock",
    on_request: "https://schema.org/BackOrder",
    out_of_stock: "https://schema.org/OutOfStock",
  };
  const availability =
    AVAILABILITY_MAP[product?.is_in_stock] || "https://schema.org/OutOfStock";

  return (
    <Container>
      <Helmet>
        <html lang={i18n.language} />
        {/* Title */}
        <title>
          {product?.name ? `${product.name} | Nico SnusCo` : "Nico SnusCo"}
        </title>

        {/* Meta Description & Keywords */}
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content={product?.name} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={ogImage} />

        {/* Canonical & Hreflang */}
        <link rel="canonical" href={productUrl} />
        <link rel="alternate" hreflang="en" href={enUrl} />
        <link rel="alternate" hreflang="de" href={deUrl} />
        <link rel="alternate" hreflang="x-default" href={enUrl} />

        {/* JSON-LD za Product */}
        {product && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: product.name,
              description: metaDescription,
              sku: product.sku,
              brand: { "@type": "Brand", name: product.category_name },
              image: ogImage,
              offers: {
                "@type": "Offer",
                priceCurrency: currencyCode,
                price: product.price,
                itemCondition: "https://schema.org/NewCondition",
                availability,
                url: productUrl,
                shippingDetails: {
                  "@type": "OfferShippingDetails",
                  shippingRate: {
                    "@type": "MonetaryAmount",
                    value: transportMethods[0].price,
                    currency: currencyCode,
                  },
                  shippingDestination: {
                    "@type": "DefinedRegion",
                    addressCountry: "NO",
                  },
                  shippingMethod: transportMethods[0].name,
                },
              },
            })}
          </script>
        )}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={product?.name} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <Header />
      <ProductMain />
      <RecommendedContainer>
        <RecommendedWrapper>
          <h2 style={{ margin: "20px 0 0 0", textTransform: "uppercase" }}>
            {t("PRODUCT.RECOMMENDED_PRODUCTS")}
          </h2>
          <ProductSliderHorizontal products={recommendedProducts} />
        </RecommendedWrapper>
      </RecommendedContainer>

      <Footer />
    </Container>
  );
};

export default Product;
